// Chapter 4 "live slides" (שקפים חיים), 2.11.2026. The eight old lecture
// slides (event.png … newhabit.png, a head profile with a picture inside)
// became one tappable deck mounted by App.tsx (LiveSlides) at
// <div data-widget-anchor="ch4_slides"> in src/chapters/chapter-4.html, plus
// the ten-step map on its own at data-widget-anchor="ch4_map".
//
// Every slide keeps the old slide's three layers: the picture (memory
// anchor, inside the drawn head), the slide's own details as a choice the
// reader marks, and the full explanation folded under "עוד על השלב".
// Answers persist in progress.fields like the frames do; two keys are
// shared with existing frames so both show the same mark:
//   slide 3 → ch4_f4_0 (frame 4.4, the four reactions)
//   slide 7 → ch4_f6_0 (frame 4.6, the step where the pit stops her)
//
// Pictures: public/ch4/slides/sN.webp are TEMPORARY crops of the old slides
// until Shlomo generates the six new ones (prompts file of 2.11 in
// docs/תרגילים ומסגרות). Slide 6 reuses the lion-hug photo.

export type Slide = {
  id: string;
  n: number;
  tag: string;
  title: string;
  lead: string;
  img?: string;
  art?: "map" | "recap";
  choice?: { q: string; opts: string[]; hint: Record<string, string>; key?: string };
  breath?: boolean;
  more?: string;
};

export const CH4_MAP_STEPS: { n: number; name: string; desc: string }[] = [
  { n: 1, name: "אירוע פנימי", desc: "תחושה, מחשבה, כאב, שמחה: ביני לבין עצמי." },
  { n: 2, name: "אירוע חיצוני", desc: "מבט, משפט, מחווה, איום, חיבוק: ביני לבין הסביבה. אין אירוע, אין רגש." },
  { n: 3, name: "פרשנות מהירה", desc: "לא מודעת, תוך שבריר שנייה, לפי מערכת ההפעלה האישית. אם יש פער בין הצפוי למה שקרה, מופעלת מערכת הורמונלית." },
  { n: 4, name: "רגש", desc: "פער שלילי = רגש שלילי (פחד, כעס, בושה, תסכול, מאות רגשות שנובעים מפחד מוות). פער חיובי = רגש חיובי." },
  { n: 5, name: "תגובה אוטומטית", desc: "להילחם, לברוח, לקפוא. או, רצוי: עצירה ומעבר לחשיבה מודעת." },
  { n: 6, name: "פריצת המודעות", desc: "נקודת ההתפקחות. אני עוצרת ושואלת: יש כאן באמת פחד מוות?" },
  { n: 7, name: "השהיה", desc: "שש שניות שבהן מנסים לא לחשוב. כך נמצא יותר פרשנויות אפשריות." },
  { n: 8, name: "פרשנות שנייה", desc: "חזרה לחשיבה שמחפשת פרשנות אחרת: קבלה של עצמי, של האחר, הבנה שאנחנו שונים." },
  { n: 9, name: "רגש מתוך מודעות", desc: "באופן טבעי עולה רגש מאוזן ואפילו חיובי. הצלחנו לא להיכנס למסע של רגשות שליליים." },
  { n: 10, name: "תגובה מחושבת", desc: "קשת אפשרויות בלתי מוגבלת. נכנסים לעמדת מנהיג של עצמנו." },
];

export const CH4_MAP_SLIDE: Slide = {
  id: "ch4_f6_0",
  n: 7,
  tag: "שלב שביעי: המפה כולה",
  title: "עשרה שלבים, מהאירוע אל הרגש המיטבי",
  lead: "לפני שלב 6 הכל אוטומטי. אחריו הכל בחירה. הקישי על השלב שאליו הבור שלך מגיע בדרך כלל לפני שהאוטומט לוקח.",
  art: "map",
  more:
    "<p><b>מה בין האדום לירוק.</b> חמשת השלבים הראשונים קורים לבד, בשבריר שנייה, ואי אפשר למנוע אותם. שלב 6 הוא הגשר: הרגע היחיד שבו יש בחירה. ארבעת השלבים שאחריו הם מה שקורה כשבחרת.</p><p><b>האירוע הבא.</b> התרשים הוא מעגל: התגובה שלך, אוטומטית או מחושבת, היא כבר האירוע הבא של מישהו, ולפעמים שלך. לכן הרגל קטן בשלב 6 משנה הרבה יותר מאירוע אחד.</p><p><b>96%.</b> ביותר מתשעים ושישה אחוזים מהמקרים שבהם נתנו פרשנות שלילית, האירוע כלל לא היה שלילי. מה שהדליק את הרגש הוא הד מוחלש לאיום קדום.</p>",
};

export const CH4_SLIDES: Slide[] = [
  {
    id: "ch4_s1",
    n: 1,
    tag: "שלב ראשון: האירוע",
    title: "ההתחלה שלא מפסיקה להתחיל",
    lead: "כל רגש מתחיל באירוע: במחשבה, בגוף, ברגש, ומחמשת החושים. אין אירוע, אין רגש.",
    img: "/ch4/slides/s1.webp",
    choice: {
      q: "הבור שלך, מהמסגרת הראשונה: איפה האירוע שלו קורה?",
      opts: ["ביני לבין עצמי", "ביני לבין הסביבה", "גם וגם"],
      hint: {
        "ביני לבין עצמי": "אירוע פנימי: מחשבה שעולה, כאב, זיכרון. אף אחד מבחוץ לא ראה שמשהו קרה, ובכל זאת הרגש עלה.",
        "ביני לבין הסביבה": "אירוע חיצוני: מילה, מבט, מחווה. המפגש עם מישהו אחר הדליק אותו.",
        "גם וגם": "כמו רוב האירועים. שיחה סתמית מציתה כאב ישן, ומחשבה קטנה משנה איך את חווה אדם אחר. שני העולמות נפגשים בתוכך כל הזמן.",
      },
    },
    more:
      "<p><b>שני צירים.</b> הפנימי: רגשות, תחושות, מחשבות, זיכרונות. החיצוני: מגע, שיח, מבטים, מפגש עם אנשים. ולמרות שנדמה שיש הבחנה ברורה, בפועל הם כמעט תמיד שזורים זה בזה.</p><p><b>שני סוגים של אינטליגנציה רגשית.</b> פנימית: היכולת להבין, להכיל ולנהל את הרגשות שלי. חברתית: היכולת להבין ולהתנהל מול הרגשות של אחרים. מי שחזקה בפנימית יודעת להכיל את עצמה, אך ייתכן שתתקשה לזהות רגשות אצל אחרים, ולהיפך.</p>",
  },
  {
    id: "ch4_s2",
    n: 2,
    tag: "שלב שני: הפרשנות הראשונה",
    title: "פרשנות לא מודעת, בחלקי שנייה",
    lead: "המוח המהיר משווה את מה שקרה למה שהוא מכיר כנורמלי, ומחליט לפנייך: סכנה, חיובי, או לא קרה כלום.",
    img: "/ch4/slides/s2.webp",
    choice: {
      q: "בבור שלך, מה המוח המהיר החליט?",
      opts: ["אין חריגה", "פער חיובי", "פער שלילי"],
      hint: {
        "אין חריגה": "לא נוצר רגש. אם זה בכל זאת הבור שלך, כנראה שהפרשנות הייתה אחרת ממה שנדמה: חזרי ובדקי.",
        "פער חיובי": "עולה רגש חיובי. נדיר בבור, אבל קורה: לפעמים הבור הוא דווקא התרגשות יתר, ציפייה, אופוריה.",
        "פער שלילי": "עולה רגש שלילי. מערכת ההישרדות נטלה שליטה ושאלה שאלה אחת בלבד: האם יש כאן סכנת מוות?",
      },
    },
    more:
      "<p><b>מה קובע את הפרשנות.</b> מערכת ההפעלה האישית: תורשה, האינטליגנציות השונות, זיכרון וחוויות חיים, חינוך, ערכים ואמונות, המצב הרגשי והגופני ברגע האירוע (ערנות, רעב, עייפות), ומה שנאמר לך קודם.</p><p><b>הכלל מהתעופה.</b> המוח ההישרדותי פועל לפי \"אם יש ספק, אין ספק\": לא ממריאים. לכן ברוב המקרים התת-מודע טועה ומזהה איום, ואילו המוח המודע היה מזהה אירוע מאוזן או אפילו חיובי.</p><p><b>שלוש שכבות במוח</b> (כך הופיע בשקף המקורי): הקליטה החושית הראשונית, העיבוד המשני, והקישור לזיכרונות. שלושתן קורות בחלקי שנייה, לפני שאת יודעת שמשהו קרה.</p>",
  },
  {
    id: "ch4_f4_0",
    n: 3,
    tag: "שלב שלישי: הרגש ההישרדותי",
    title: "נקודת ההתפקחות: ההזדמנות והשינוי",
    lead: "הרגש עלה, והגוף כבר בחר: להילחם, לברוח, לקפוא. ובדיוק כאן, בשבריר השנייה הזה, יש נקודה אחת שבה אפשר לעצור.",
    img: "/ch4/slides/s3.webp",
    choice: {
      q: "בבור שלך, מה הגוף בוחר בדרך כלל? (אותה שאלה תופיע גם במסגרת 4.4)",
      opts: ["להילחם", "לברוח", "לקפוא", "להדחיק"],
      hint: {
        "להילחם": "התגובה: תקיפה חזיתית, שתלטנות. הרגש: כעס, חוסר שקט, תוקפנות. התוצאה: אשמה, העלבה, פגיעה.",
        "לברוח": "התגובה: יציאה מהחדר, התחמקות, ניתוק. הרגש: התכחשות, חרדה, רצון להיעלם. התוצאה: התעלמות, פגיעה שלא טופלה.",
        "לקפוא": "התגובה: שתיקה, כניעה, שקט. הרגש: ריקנות, הסתגרות. התוצאה: הצדקה עצמית, התנתקות.",
        "להדחיק": "ההדחקה לא מעלימה את האנרגיה, רק דוחסת אותה פנימה, והיא מחפשת דרך החוצה בהמשך. עדיף להאיר את הרגש למודע, בעדינות.",
      },
    },
    more:
      "<p><b>שלוש תגובות עתיקות.</b> נועדו להגן עלינו ממוות פיזי ממשי. בעולם המודרני הן מגיבות לאזעקות שווא: המוח ההישרדותי ממשיך להגיב לאיום חברתי כאילו היה איום קיומי, כי האבולוציה לא הספיקה להתאים את הקצב שלו לחיים שלנו.</p><p><b>נקודת ההתפקחות.</b> לזהות רגש שלילי שעלה מתוך המוח הלא מודע, לעבור למרחב מודע, להתרחק צעד אחד מהאוטומט, ולשאול: האם באמת יש כאן סכנת מוות? ואם הגעת לשאלה, התשובה כבר ברורה: אילו הייתה סכנה אמיתית, לא היית שואלת.</p>",
  },
  {
    id: "ch4_s4",
    n: 4,
    tag: "שלב רביעי: שש שניות",
    title: "השהיה: משחררים, נושמים עמוק, ושוב פעם, ואז...",
    lead: "שש שניות שבהן מנסים לא לחשוב. השהיה קצרה, וכבר יש בה מקום ליותר מפרשנות אחת.",
    img: "/ch4/slides/s4.webp",
    breath: true,
    more:
      "<p><b>למה דווקא שש.</b> הורמוני הלחץ שהופרשו בשלב הקודם צריכים כמה שניות כדי להתפזר. כשאת נושמת עמוק ולא חושבת, את נותנת לגוף את הזמן הזה, והמוח האיטי מקבל את ההגה.</p><p><b>רוב האנשים לא עוצרים שם.</b> הם נסחפים עם הרגש: מגיבים, פועלים, מצטערים. מי שמצליחה לעצור מגלה עולם אחר. שם מתחיל החופש הרגשי האמיתי.</p>",
  },
  {
    id: "ch4_s5",
    n: 5,
    tag: "שלב חמישי: הפרשנות השנייה",
    title: "פרשנות מודעת: מאוזנת, תועלתית",
    lead: "אותן עיניים, מבט אחר. אם עצרת, סימן שאת בטוחה, ומכאן אפשר לחפש פרשנות שלא נולדה מפחד.",
    img: "/ch4/slides/s5.webp",
    choice: {
      q: "איזו פרשנות שנייה מתאימה לבור שלך?",
      opts: ["קבלה של עצמי", "קבלה של האחר", "אנחנו פשוט שונים"],
      hint: {
        "קבלה של עצמי": "\"לעיתים גם אני...\": מה שעשיתי או הרגשתי הוא אנושי, ואפשר להמשיך משם בלי לשפוט.",
        "קבלה של האחר": "\"לעיתים גם הוא או היא...\": גם למי שמולי יש מערכת הפעלה משלו, והיא לא מכוונת נגדי.",
        "אנחנו פשוט שונים": "אין שתי מערכות הפעלה זהות. רוב אי ההבנות נובעות מפער, לא מכוונה רעה.",
      },
    },
    more:
      "<p><b>זה פחות מורכב ממה שזה נשמע.</b> עצם קיומה של נקודת ההתפקחות היא האות לשחרור: אם הגענו לרגע שבו שואלים, כבר ברור שלא מדובר בסכנת חיים מיידית.</p><p><b>הדלת לתגובה חדשה.</b> מכאן נפתחת תגובה רגועה ומדויקת יותר, לא דרך פחד אלא דרך תבונה. זו אינטליגנציה רגשית בפעולה.</p>",
  },
  {
    id: "ch4_s6",
    n: 6,
    tag: "שלב שישי: הרגש מתוך מודעות",
    title: "רגש תועלתי עולה: מאוזן, מקבל, ואולי אפילו חיובי",
    lead: "כשמערכת ההישרדות נרגעת, מתאפשר איזון. ואם בחרת באהבה כמנוע, עולה אחריו רגש חיובי.",
    img: "/Woman_lion_hug.webp",
    choice: {
      q: "איזה רגש היה עולה בבור שלך, אחרי הפרשנות השנייה?",
      opts: ["הקלה", "חמלה", "סיפוק שקט", "תחושת גדילה"],
      hint: {
        "הקלה": "הגוף משחרר: הכתפיים יורדות, הנשימה חוזרת. זה הסימן שהשאלה נשאלה בזמן.",
        "חמלה": "כלפי עצמך או כלפי מי שמולך. היא עולה לבד כשמבינים שאף אחד לא רצה לפגוע.",
        "סיפוק שקט": "לא ניצחון, לא דרמה. הידיעה שהפעם לא נפלת לבור, וזה הספיק.",
        "תחושת גדילה": "משהו בך התרחב. אותו אירוע, ותגובה שלא הייתה לך קודם.",
      },
    },
    more:
      "<p><b>לא חוסר הסכמה, חוסר הבנה.</b> רבים מהקונפליקטים בינינו לבין אחרים אינם נובעים מחוסר הסכמה אלא מחוסר הבנה. כשהרגשות סוערים, קשה להקשיב וקשה לדייק. כשהם מאוזנים, פתאום שומעים.</p>",
  },
  CH4_MAP_SLIDE,
  {
    id: "ch4_s8",
    n: 8,
    tag: "שלב שמיני: ההרגל",
    title: "חזרתיות מתמשכת תביא לפיתוח הרגל חדש",
    lead: "אותן עיניים, שוב ושוב, עד שצומח ענף חדש. כל פעם שעצרת בשלב 6, הבור נהיה פחות עמוק.",
    img: "/ch4/slides/s8.webp",
    choice: {
      q: "בשבוע האחרון, כמה פעמים הצלחת לעצור בבור לפני התגובה?",
      opts: ["אף פעם, עדיין", "פעם אחת", "כמה פעמים", "כמעט תמיד"],
      hint: {
        "אף פעם, עדיין": "זו נקודת ההתחלה של כולם. העובדה שאת יודעת עכשיו איפה הבור כבר שינתה משהו.",
        "פעם אחת": "פעם אחת זה הוכחה: זה אפשרי. ההרגל נבנה מהפעם השנייה.",
        "כמה פעמים": "זה כבר הרגל בבנייה. שימי לב מה היה שונה בפעמים שהצלחת: שעה, שינה, אדם.",
        "כמעט תמיד": "הבור הזה כמעט לא בור יותר. אולי הגיע הזמן לבחור את הבור הבא.",
      },
    },
    more:
      "<p><b>כך נולד הרגל.</b> רוב ההחלטות והתגובות שלנו אינן תוצאה של חשיבה מודעת אלא של הרגלים: דפוסים אוטומטיים שנצרבים בנו עם הזמן בלי שנבחין. חוזרים על אותה פעולה בנסיבות דומות, יום אחרי יום, והיא הופכת לטבע שני.</p><p><b>אותו מנגנון בונה גם את ההרגל החדש.</b> פרשנות שקולה, פעם אחר פעם, עד שהיא נעשית אוטומטית בעצמה. הרגל טוב מחליף הרגל רע באותה דרך שבה הרע נוצר: בחזרה.</p>",
  },
  {
    id: "ch4_recap",
    n: 9,
    tag: "סיכום: מה סימנת",
    title: "המפה שלי",
    lead: "כל מה שסימנת בשמונת השלבים, במקום אחד. זה נשמר יחד עם שאר התשובות שלך, ואפשר לחזור ולשנות.",
    art: "recap",
  },
];
